// characters: прогресс игрока — выбранный персонаж, серебро, уровни апгрейдов.
// Аккаунт: всё на сервере (/api/progress*), клиент только показывает.
// Гость: в localStorage; сервер верит гостю максимум до guestMaxLevel.

import { STAT_KEYS, isValidCharacterId, characterById, type StatKey } from "./registry";
import { t } from "../i18n";

export interface ProgressRules {
  maxLevel: number;
  guestMaxLevel: number;
  perLevelPct: Record<StatKey, number>;
  priceStep: number;
  earn: {
    playerKill: number;
    npcKill: number;
    npcSessionCap: number;
    roundWin: number;
    matchFinished: number;
    // artifacts: реликвии из разрушенных предметов.
    relicMedallion: number;
    relicSkull: number;
    relicCrystal: number;
    relicSessionCap: number;
  };
}

/** Зеркало server/internal/progress (используется, пока /api/progress не ответил). */
export const DEFAULT_RULES: ProgressRules = {
  maxLevel: 5,
  guestMaxLevel: 2,
  perLevelPct: { hp: 3, speed: 2, armor: 3 },
  priceStep: 40,
  earn: {
    playerKill: 10, npcKill: 2, npcSessionCap: 40, roundWin: 15, matchFinished: 10,
    relicMedallion: 10, relicSkull: 20, relicCrystal: 40, relicSessionCap: 40, // artifacts
  },
};

export interface PlayerProgress {
  guest: boolean;
  charId: number;
  coins: number;
  earnedTotal: number;
  levels: Record<StatKey, number>;
  rules: ProgressRules;
}

const LS_PROGRESS = "tipaduma.progress.v1";
const LS_CHAR = "tipaduma.char";

function zeroLevels(): Record<StatKey, number> {
  return { hp: 0, speed: 0, armor: 0 };
}

function clampLevel(v: unknown, max: number): number {
  const n = Math.floor(Number(v));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(max, n);
}

// ─── Локальное хранилище (гость + кэш выбранного персонажа) ─────────────

interface GuestStore { coins: number; earnedTotal: number; levels: Record<StatKey, number> }

function readGuestStore(rules: ProgressRules): GuestStore {
  const out: GuestStore = { coins: 0, earnedTotal: 0, levels: zeroLevels() };
  try {
    const raw = localStorage.getItem(LS_PROGRESS);
    if (!raw) return out;
    const j = JSON.parse(raw) as Partial<GuestStore>;
    out.coins = Math.max(0, Math.floor(Number(j.coins) || 0));
    out.earnedTotal = Math.max(0, Math.floor(Number(j.earnedTotal) || 0));
    for (const k of STAT_KEYS) out.levels[k] = clampLevel(j.levels?.[k], rules.maxLevel);
  } catch { /* приватный режим / битый JSON — начинаем с нуля */ }
  return out;
}

function writeGuestStore(s: GuestStore): void {
  try { localStorage.setItem(LS_PROGRESS, JSON.stringify(s)); } catch { /* ignore */ }
}

/** Последний выбранный персонаж в этом браузере (для гостя — единственный источник). */
export function readLocalCharId(): number {
  try {
    const v = parseInt(localStorage.getItem(LS_CHAR) ?? "", 10);
    return isValidCharacterId(v) ? v : 0;
  } catch {
    return 0;
  }
}

export function writeLocalCharId(id: number): void {
  if (!isValidCharacterId(id)) return;
  try { localStorage.setItem(LS_CHAR, String(id)); } catch { /* ignore */ }
}

// ─── Правила (цены и эффекты) ──────────────────────────────────────────

export function upgradePrice(nextLevel: number, rules: ProgressRules): number {
  if (nextLevel < 1 || nextLevel > rules.maxLevel) return 0;
  return rules.priceStep * nextLevel;
}

/** Купленные уровни → эффективные (зеркало progress.NewLoadout на сервере). */
export function effectiveLevels(
  charId: number, bought: Record<StatKey, number>, guest: boolean, rules: ProgressRules,
): Record<StatKey, number> {
  const cap = guest ? rules.guestMaxLevel : rules.maxLevel;
  const out = zeroLevels();
  for (const k of STAT_KEYS) out[k] = Math.min(cap, clampLevel(bought[k], rules.maxLevel));
  const b = characterById(charId).bonus;
  out[b] = Math.min(rules.maxLevel, out[b] + 1);
  return out;
}

/** «+9 %» / «−9 % урона» для подписи стата. */
export function effectLabel(stat: StatKey, level: number, rules: ProgressRules): string {
  const pct = rules.perLevelPct[stat] * level;
  if (pct === 0) return t("база");
  if (stat === "armor") return t("−{pct} % урона", { pct });
  if (stat === "hp") return `${100 + pct} HP`;
  return `+${pct} %`;
}

// ─── Сеть ──────────────────────────────────────────────────────────────

function parseServerProgress(j: unknown): PlayerProgress | null {
  if (!j || typeof j !== "object") return null;
  const o = j as Record<string, unknown>;
  const r = (o.rules ?? {}) as Partial<ProgressRules>;
  const rules: ProgressRules = {
    maxLevel: Number(r.maxLevel) || DEFAULT_RULES.maxLevel,
    guestMaxLevel: Number(r.guestMaxLevel) || DEFAULT_RULES.guestMaxLevel,
    perLevelPct: { ...DEFAULT_RULES.perLevelPct, ...(r.perLevelPct ?? {}) },
    priceStep: Number(r.priceStep) || DEFAULT_RULES.priceStep,
    earn: { ...DEFAULT_RULES.earn, ...(r.earn ?? {}) },
  };
  const lv = (o.levels ?? {}) as Record<string, unknown>;
  const levels = zeroLevels();
  for (const k of STAT_KEYS) levels[k] = clampLevel(lv[k], rules.maxLevel);
  const charId = Number(o.charId);
  return {
    guest: o.guest === true,
    charId: isValidCharacterId(charId) ? charId : 0,
    coins: Math.max(0, Math.floor(Number(o.coins) || 0)),
    earnedTotal: Math.max(0, Math.floor(Number(o.earnedTotal) || 0)),
    levels,
    rules,
  };
}

/** Прогресс для меню. Гостю (или без сети) — локальный. */
export async function fetchProgress(): Promise<PlayerProgress> {
  let p: PlayerProgress | null = null;
  try {
    const resp = await fetch(`${location.origin}/api/progress`, { credentials: "include" });
    if (resp.ok) p = parseServerProgress(await resp.json());
  } catch { /* офлайн / старый сервер — как гость */ }
  if (!p || p.guest) {
    const rules = p?.rules ?? DEFAULT_RULES;
    const g = readGuestStore(rules);
    return { guest: true, charId: readLocalCharId(), coins: g.coins, earnedTotal: g.earnedTotal,
      levels: g.levels, rules };
  }
  writeLocalCharId(p.charId);
  return p;
}

/** Запомнить персонажа: аккаунту — на сервере (+ локально), гостю — локально. */
export async function saveCharacter(p: PlayerProgress, charId: number): Promise<void> {
  if (!isValidCharacterId(charId)) return;
  writeLocalCharId(charId);
  p.charId = charId;
  if (p.guest) return;
  try {
    await fetch(`${location.origin}/api/progress/character`, {
      method: "POST", credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ charId }),
    });
  } catch { /* не страшно: ?char= в WS всё равно применит и запомнит выбор */ }
}

export type BuyResult = { ok: true; progress: PlayerProgress } | { ok: false; error: string };

/** Купить следующий уровень стата. */
export async function buyUpgrade(p: PlayerProgress, stat: StatKey): Promise<BuyResult> {
  const cur = p.levels[stat];
  const cap = p.guest ? p.rules.guestMaxLevel : p.rules.maxLevel;
  if (cur >= cap) {
    return { ok: false, error: p.guest && cur < p.rules.maxLevel
      ? t("Гостю доступно до {cap} уровня — войдите, чтобы качать дальше", { cap })
      : t("Уровень уже максимальный") };
  }
  const price = upgradePrice(cur + 1, p.rules);
  if (p.coins < price) return { ok: false, error: t("Нужно {price} серебра", { price }) };
  if (p.guest) {
    const g = readGuestStore(p.rules);
    g.coins -= price;
    g.levels[stat] = cur + 1;
    writeGuestStore(g);
    return { ok: true, progress: { ...p, coins: g.coins, levels: { ...g.levels } } };
  }
  try {
    const resp = await fetch(`${location.origin}/api/progress/upgrade`, {
      method: "POST", credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ stat }),
    });
    const j = await resp.json().catch(() => null) as Record<string, unknown> | null;
    if (!resp.ok) return { ok: false, error: j?.error ? t(String(j.error).trim()) : t("Ошибка {status}", { status: resp.status }) };
    const np = parseServerProgress(j);
    return np ? { ok: true, progress: np } : { ok: false, error: t("Ошибка ответа сервера") };
  } catch {
    return { ok: false, error: t("Нет связи с сервером") };
  }
}

/** Гость: начислить серебро локально (сервер гостям не начисляет). */
export function creditGuestCoins(amount: number): void {
  if (amount <= 0) return;
  const g = readGuestStore(DEFAULT_RULES);
  g.coins += amount;
  g.earnedTotal += amount;
  writeGuestStore(g);
}

/** Гость: уровни для ?lv= при входе в комнату (сервер урежет до guestMaxLevel). */
export function guestLevelsParam(): string {
  const g = readGuestStore(DEFAULT_RULES);
  return STAT_KEYS.map((k) => g.levels[k]).join(",");
}
